import { Box, Button, CircularProgress, FormControl, Grid, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { RootState } from '../../../app/store';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { GridDeleteIcon } from '@mui/x-data-grid';
import ConfirmDialogList from '../../../components/ConfirmDialogList';
import configUrls from '../../../config/configUrls';

import { ErrorMessage } from '../../../types/ErrorMessage';
import { ISprocessoSetores, ISprocessoSetoresList, ProcessoSetores, ProcessoSetoresList } from '../../../types/processo/ProcessoSetoresDTO';
import { useDeleteSobrestadoMutation, useGetSobrestadosQuery } from './processoSobrestarApiSlice';
import ConfirmDialogOkButton from '../../../components/token/ConfirmDialogOkButton';

export const ProcessoSobrestarListForm = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate(); // Hook para navegação

  const [sobrestados, setSobrestados] = useState<ProcessoSetoresList[]>([]);

  const [sobrestadoToDelete, setSobrestadoToDelete] = useState<ProcessoSetores>(ISprocessoSetores);
  const [openConfirmExcluir, setOpenConfirmExcluir] = useState(false);
  const [deleteSobrestado, { error: deleteError, isLoading, isSuccess }] = useDeleteSobrestadoMutation();

  const [sobrestadoToDetails, setSobrestadoToDetails] = useState<ProcessoSetoresList>(ISprocessoSetoresList);
  const [openConfirmDetails, setOpenConfirmDetails] = useState(false);

  const { processoSelecionado } = useSelector((state: RootState) => state.processoSelecionado);

  useEffect(() => {
    if (!processoSelecionado) {
      alert('Nenhum processo selecionado');
      window.location.href = configUrls.PROCESSO_LIST_RT;
    }
  }, [processoSelecionado]);

  /*SEARCH*/

  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cursorPosition = useRef<number>(0);

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    cursorPosition.current = inputRef.current?.selectionStart || 0;
    setSearch(e.target.value);
  };

  useEffect(() => {
    // Limpar o timeout anterior
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // Configurar o timeout para o debounce
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 700); // Tempo de debounce (700ms)

    // Limpar o timeout quando o componente for desmontado ou o efeito for reiniciado
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [search]); // Dependência do useEffect: quando o 'search' mudar

  const { data, isFetching, error } = useGetSobrestadosQuery({ id: processoSelecionado?.id ?? '', search: debouncedSearch });

  useEffect(() => {
    if (data) {
      setSobrestados(data);
    }
    if (error) {
      enqueueSnackbar('Envios não encontrados!', { variant: 'error' });
    } else if (isFetching) {
      enqueueSnackbar('Buscando registros...', { variant: 'info' });
    }

    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current?.setSelectionRange(cursorPosition.current, cursorPosition.current);
    }
  }, [data, error, isFetching, enqueueSnackbar]);
  /*FIM SEARCH*/

  const headerGerados = (
    <Typography variant="body2" color="textSecondary">
      Sobrestamento(s) encontrado(s): {sobrestados.length}
    </Typography>
  );

  const renderData = (rowData: ProcessoSetoresList) => {
    const dataCriacao = rowData.dataSobrestado ? format(rowData.dataSobrestado, 'dd/MM/yyyy HH:mm:ss') : 'Data não disponível';
    return (
      <Typography color="secondary" variant="body2">
        {dataCriacao}
      </Typography>
    );
  };

  const renderUsuario = (rowData: ProcessoSetoresList) => (
    <Typography color="secondary" variant="body2">
      {rowData.usuarioSobrestado?.nome || 'Usuário não disponível'}
    </Typography>
  );

  const renderSetor = (rowData: ProcessoSetoresList) => (
    <Typography color="secondary" variant="body2" style={{ fontWeight: rowData.usuarioRecibo?.nome ? 'normal' : 'bold' }}>
      {rowData.setor.secretaria?.orgao?.abreviatura + ' - ' + rowData.setor.secretaria?.abreviatura + ' - ' + rowData.setor.nome || ''}
    </Typography>
  );

  const renderAction = (rowData: any) => {
    return (
      <Box>
        <Tooltip title="Excluir sobrestamento">
          <IconButton color="secondary" aria-label="delete" onClick={() => handleExcluirClick(rowData)}>
            <GridDeleteIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Observação do sobrestamento">
          <IconButton color="secondary" aria-label="delete" onClick={() => handleDetalhesClick(rowData)}>
            <InfoOutlinedIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const handleExcluirClick = (rowData: any) => {
    setSobrestadoToDelete(rowData);
    setOpenConfirmExcluir(true);
  };

  const handleDetalhesClick = (rowData: any) => {
    setSobrestadoToDetails(rowData);
    setOpenConfirmDetails(true);
  };
  const handleCloseDialog = () => {
    setOpenConfirmExcluir(false);
    setOpenConfirmDetails(false);
  };
  const handleConfirm = async () => {
    setOpenConfirmExcluir(false);
    await deleteSobrestado({ id: sobrestadoToDelete.id ? sobrestadoToDelete.id : '' });
  };

  useEffect(() => {
    if (isSuccess) {
      enqueueSnackbar('Registro deletado com sucesso!', { variant: 'success' });
      // fetchDados();
    }
    if (deleteError) {
      let msgErro = 'Registro não deletado!';
      if ('data' in deleteError) {
        const erroMensagem = deleteError.data as ErrorMessage;
        if (erroMensagem?.message) {
          msgErro = erroMensagem.message;
        }
      }
      enqueueSnackbar(msgErro, { variant: 'error' });
    }
  }, [isSuccess, deleteError]);

  if (isFetching || loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <ConfirmDialogList
        open={openConfirmExcluir}
        handleClose={handleCloseDialog}
        title="Confirmar Ação"
        text={['Você tem certeza de que deseja excluir esse Sobrestamento?']}
        handleDelete={() => {
          handleConfirm(); // Chama a função handleConfirm
          setOpenConfirmExcluir(false); // Fecha o diálogo
        }}
      />
      <ConfirmDialogOkButton open={openConfirmDetails} onClose={handleCloseDialog} title="Obervação" message={sobrestadoToDetails.observacaoSobrestado} />

      <Paper sx={{ width: '100%', height: '100vh', padding: '12px', display: 'flex', flexDirection: 'column' }}>
        <Grid container padding={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Box display="flex">
                  <Typography color="secondary" variant="h6" gutterBottom>
                    Sobrestamentos do processo: {processoSelecionado?.numeroProcesso}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      navigate(configUrls.PROCESSO_SOBRESTAR_CREATE_RT);
                    }}
                  >
                    Novo
                  </Button>

                  <Button variant="contained" color="secondary" onClick={() => navigate(-1)}>
                    Voltar
                  </Button>
                </Box>
              </Box>
            </FormControl>
          </Grid>

          <Grid item xs={12} mt={5}>
            <FormControl fullWidth>
              <TextField label="Digite para pesquisar" name="nome" onChange={handleChangeSearch} size="small" inputProps={{ maxLength: 100 }} value={search} inputRef={inputRef} />
            </FormControl>
          </Grid>
        </Grid>

        <DataTable
          value={sobrestados}
          paginator
          rows={25}
          showGridlines={true}
          size="small"
          columnResizeMode="expand"
          resizableColumns
          dataKey="id"
          header={headerGerados}
          className="custom-datatable"
        >
          <Column header="Data" body={renderData} sortable style={{ width: '20%' }} />
          <Column header="Setor" body={renderSetor} sortable style={{ width: '70%' }} />
          <Column header="Sobrestado por:" body={renderUsuario} sortable style={{ width: '20%' }} />
          <Column field="acoes" header="Ações" body={renderAction} style={{ width: 'auto' }}></Column>
        </DataTable>
      </Paper>
    </>
  );
};
